/**
 * Vanilla block-breaking rules: hardness, harvest requirements, break time,
 * drops, tool wear, XP and the canonical inventory id of a block.
 *
 * API (all pure, DOM-free):
 *   blockHardness(id)            vanilla hardness, -1 = unbreakable (bedrock, fluids)
 *   requiredTool(id)             what harvests it, or null (anything)
 *   canHarvest(id, heldId)       does breaking it with `heldId` yield drops?
 *   breakSeconds(id, held, ctx)  vanilla break time (hardness*1.5|5 / speed, x5 airborne, x5 water)
 *   dropsFor(id, held, rnd)      Stack[] (honours harvest rules). Doors/beds: ONLY the primary
 *                                half (door bottom, bed foot) drops — the other half returns [],
 *                                so breaking both halves never duplicates the item.
 *   blockItemOf(id)              canonical inventory id (facing/open/half/wall variants collapse)
 *   toolWear(id, held)           durability cost of breaking this block with this item
 *   experienceFor(id, rnd)       XP value for ores (call only when the block was harvested)
 *
 * Tables are built once at load from the block's display name (the registry has
 * ~300 ids and whole families share names), with an explicit rule per family.
 */

import { cropDrops, isCrop } from './farming';
import {
  AIR, AZALEA_ID, BEDROCK_ID, BED_FOOT_ID, BED_HEAD_ID, BLOCKS, BIRCH_LOG_ID, CHEST_FACINGS, CHEST_OPEN_FACING_0, CHEST_OPEN_FACING_1,
  CHEST_OPEN_FACING_2, CHEST_OPEN_FACING_3, COBBLE_ID, COBBLED_DEEPSLATE_ID, COBWEB_ID, DEEPSLATE_ID, DIRT_ID,
  ANVIL_FAMILIES, DIRT_PATH_ID, DOOR_STATE, DOOR_TOP_IDS, FALLEN_LOG_IDS, FARMLAND_DRY_ID, FARMLAND_ID, FIRE_ID, FURNACE_FACINGS, FURNACE_ID, FURNACE_KINDS,
  GRASS_ID, GRAVEL_ID, LOG_ID, MELON_ID, MYCELIUM_ID, PODZOL_ID, SNOW_ID, SPRUCE_LOG_ID, STONE_ID, TALL_GRASS_ID,
  FERN_ID, DEAD_BUSH_ID, WHEAT_ID, CLAY_ID, SEAGRASS_ID, TORCH_WALLS, OAK_SAPLING_ID,
  RED_MUSHROOM_BLOCK_ID, BROWN_MUSHROOM_BLOCK_ID, WATER_ID, LAVA_ID, FLOWING_LAVA_IDS, FALLING_LAVA_ID, FLOWING_WATER_IDS, FALLING_WATER_ID,
  GLASS_ID, ICE_ID, PACKED_ICE_ID, BLUE_ICE_ID, POWDER_SNOW_ID, SNOW_BLOCK_ID, SNOW_LAYER_ID, SCULK_ID,
  QUARTZ_ORE_ID, GLOWSTONE_ID, BOOKSHELF_ID, SEA_LANTERN_ID, doorId, SCULK_SHRIEKER_ID, SCULK_SHRIEKER_SUMMON_ID, SCULK_CATALYST_ID,
  RAIL_FAMILIES, SHAPE_FAMILIES, SHAPE_STATE, NETHER_BRICK_FENCE_ID, NETHER_BRICK_STAIRS_IDS, IRON_BARS_ID,
} from '../blocks';
import { I, itemNameOf } from './itemIds';
import { UK, UTIL, U_A, U_GROUPS, U_KIND } from './utility/defs';
import { K, RS_GROUPS, RS_KIND } from '../world/redstone/defs';
import { itemDef, type ToolType } from './itemdb';
import { stackOf, type Stack } from './types';

// --- tools ------------------------------------------------------------------------

interface ToolInfo { type: ToolType; tier: number; speed: number }

const TIER_OF: Record<string, [number, number]> = { WOODEN: [0, 2], STONE: [1, 4], IRON: [2, 6], GOLDEN: [0, 12], DIAMOND: [3, 8] };

/** Tool stats of an item: the item db when it knows, else derived from the registry name. */
function toolOf(heldId: number): ToolInfo | null {
  if (heldId <= 0) return null;
  const t = itemDef(heldId).tool;
  if (t) return t;
  const n = itemNameOf(heldId);
  if (!n) return null;
  if (n === 'SHEARS') return { type: 'shears', tier: 0, speed: 1.5 };
  const m = /^(WOODEN|STONE|IRON|GOLDEN|DIAMOND)_(SWORD|PICKAXE|AXE|SHOVEL|HOE)$/.exec(n);
  if (!m) return null;
  const [tier, speed] = TIER_OF[m[1]];
  return { type: m[2].toLowerCase() as ToolType, tier, speed };
}

// --- per-block spec -----------------------------------------------------------------

interface Spec {
  /** Vanilla hardness; -1 unbreakable. */
  h: number;
  /** Tool class that mines it fast (null = no class: hand speed for everything). */
  tool: ToolType | null;
  /** Minimum tier of `tool` needed to harvest; undefined = harvestable by anything. */
  need?: number;
}

const rock = (h: number, need = 0): Spec => ({ h, tool: 'pickaxe', need });
const rockFree = (h: number): Spec => ({ h, tool: 'pickaxe' });
const wood = (h: number): Spec => ({ h, tool: 'axe' });
const dirt = (h: number): Spec => ({ h, tool: 'shovel' });
const hoe = (h: number): Spec => ({ h, tool: 'hoe' });
const none = (h: number): Spec => ({ h, tool: null });
const FLUID: Spec = { h: -1, tool: null };

/** First match wins, so more specific names come first. */
const RULES: Array<[RegExp, Spec]> = [
  [/^(air)$/i, none(0)],
  // containers and utility blocks (game/utility/defs.ts)
  [/^Trapped Chest$/, wood(2.5)],
  [/^Ender Chest$/, rock(22.5, 0)],
  [/^Hopper$/, rock(3, 0)],
  [/^(Dispenser|Dropper)$/, rock(3.5, 0)],
  [/^Shulker Box$/, rockFree(2)],
  [/ Sign$/, wood(1)],
  [/^Cauldron$/, rock(2, 0)],
  [/^Cake$/, none(0.5)],
  [/^(Glow )?Item Frame$|^Flower Pot$/, none(0)],
  [/^Ladder$/, wood(0.4)],
  [/^Vine$/, { h: 0.2, tool: 'shears' }],
  [/^Chain$/, rock(5, 0)],
  [/^(Soul )?Lantern$/, rock(3.5, 0)],
  [/^Candle$/, none(0.1)],
  [/^Campfire$/, wood(2)],
  [/^Lightning Rod$/, rock(3, 1)],
  [/^Composter$/, wood(0.6)],
  [/^(Lectern|Barrel|Fletching Table)$/, wood(2.5)],
  [/^(Smoker|Blast Furnace|Stonecutter)$/, rock(3.5, 0)],
  [/^Grindstone$/, rock(2, 0)],
  [/^Brewing Stand$/, rockFree(0.5)],
  [/^(Polished Blackstone Bricks|Gilded Blackstone|Purpur Block|Purpur Pillar)$/, rock(1.5, 0)],
  [/^(Trial Spawner|Vault|Silverfish Spawner)$/, rock(50, 0)],
  [/^Chorus (Plant|Flower)$/, wood(0.4)],
  [/^End Gateway$/, FLUID],
  // redstone components (world/redstone)
  [/^(Redstone (Dust|Torch|Repeater|Comparator)|Tripwire( Hook)?)$/, none(0)],
  [/^(Lever|Stone Button|Oak Button)$/, none(0.5)],
  [/^Oak Pressure Plate$/, wood(0.5)],
  [/Pressure Plate$/, rockFree(0.5)],
  [/^Redstone Lamp$/, none(0.3)],
  [/^Observer$/, rock(3, 0)],
  [/^(Sticky Piston|Piston|Piston Head)$/, rockFree(1.5)],
  [/^Note Block$/, wood(0.8)],
  [/^Target$/, hoe(0.5)],
  [/^Daylight Detector$/, wood(0.2)],
  [/^Iron (Door|Trapdoor)$/, rock(5, 0)],
  [/^(Detector|Activator) Rail$/, rockFree(0.7)],
  [/^Bedrock$|^End Portal( Frame)?$/, FLUID],
  [/^(Water|Flowing Water|Falling Water|Lava|Flowing Lava|Falling Lava)$/, FLUID],
  [/^(Obsidian|Crying Obsidian)$/, rock(50, 3)],
  [/^Ancient Debris$/, rock(30, 3)],
  // ores: deepslate variants are 4.5 instead of 3, same tier
  [/^Deepslate (Coal) Ore$/, rock(4.5, 0)],
  [/^Deepslate (Iron|Copper|Lapis) Ore$/, rock(4.5, 1)],
  [/^Deepslate (Gold|Diamond|Redstone|Emerald) Ore$/, rock(4.5, 2)],
  [/^(Coal Ore|Nether Quartz Ore)$/, rock(3, 0)],
  [/^(Iron Ore|Copper Ore|Lapis Ore)$/, rock(3, 1)],
  [/^(Gold Ore|Diamond Ore|Redstone Ore|Emerald Ore)$/, rock(3, 2)],
  // metal / mineral blocks
  [/^Block of (Raw Iron|Raw Copper|Iron|Copper)$/, rock(5, 1)],
  [/^Block of Raw Gold$/, rock(5, 2)],
  [/^Block of (Diamond|Emerald)$/, rock(5, 2)],
  [/^Block of Gold$/, rock(3, 2)],
  [/^Block of Lapis$/, rock(3, 1)],
  [/^Block of (Redstone|Coal)$/, rock(5, 0)],
  [/^(Cut|Exposed|Weathered|Oxidized) Copper$/, rock(3, 1)],
  [/^Block of Amethyst$/, rock(1.5)],
  // deepslate / stone family
  [/^Deepslate$/, rock(3)],
  [/^(Cobbled|Polished) Deepslate$|^Deepslate (Bricks|Tiles)$|^Chiseled Deepslate$/, rock(3.5)],
  [/^Cobblestone$|^Mossy Cobblestone$|^Smooth Stone$/, rock(2)],
  [/^End Stone( Bricks)?$/, rock(3)],
  [/^Stone$|Stone Bricks$/, rock(1.5)],
  [/^Dragon Egg$/, none(3)],
  [/^Wither Skeleton Skull$/, none(1)],
  [/^Bricks$|^Nether Bricks$/, rock(2)],
  [/^(Polished )?(Granite|Diorite|Andesite)$|^Tuff$|^Dripstone Block$|^Pointed Dripstone$|^Hanging Pointed Dripstone$/, rock(1.5)],
  [/^Calcite$/, rock(0.75)],
  [/^(Smooth )?Basalt$/, rock(1.25)],
  [/^Smooth (Red )?Sandstone$/, rock(2)],
  [/Sandstone$/, rock(0.8)],
  [/Terracotta$/, rock(1.25)],
  [/Concrete$/, rock(1.8)],
  [/^Netherrack$/, rock(0.4)],
  [/^(Crimson|Warped) Nylium$/, rock(0.4)],
  [/^Blackstone$/, rock(1.5)],
  [/^Nether Gold Ore$/, rock(3, 0)],
  [/^Nether Portal$/, FLUID],
  [/^(Chiseled|Cracked) Nether Bricks$|^Nether Brick (Fence|Stairs)$/, rock(2)],
  [/^Spawner$/, rock(5)],
  [/^Soul Fire$/, none(0)],
  [/^Nether Wart$|Fungus$|Roots$|Vines( Plant)?$/, none(0)],
  [/^(Crimson|Warped) Stem$/, wood(2)],
  [/^(Nether|Warped) Wart Block$/, hoe(1)],
  [/^Magma Block$/, rock(0.5)],
  [/^(Prismarine|Prismarine Bricks|Dark Prismarine)$/, rock(1.5)],
  [/^Bone Block$/, rock(2)],
  [/^Quartz Block$/, rock(0.8)],
  [/^Mud Bricks$/, rock(1.5)],
  [/^Packed Mud$/, rock(1)],
  [/^Furnace$/, rock(3.5)],
  [/Coral Block$/, rock(1.5)],
  [/^(Powered )?Rail$/, rockFree(0.7)],
  // ice (breaks to nothing without silk touch) and misc translucent
  [/^(Ice|Packed Ice)$/, rockFree(0.5)],
  [/^Blue Ice$/, rockFree(2.8)],
  [/^Glass$/, none(0.3)],
  [/^(Glowstone|Sea Lantern)$/, none(0.3)],
  [/^Shroomlight$/, hoe(1)],
  // wood
  [/^(Oak|Spruce|Birch|Jungle|Acacia|Dark Oak|Mangrove|Cherry|Pale Oak) (Slab|Stairs|Fence|Fence Gate)$/, wood(2)],
  [/Trapdoor$/, wood(3)],
  [/^Glass Pane$/, none(0.3)],
  [/^Iron Bars$/, rock(5, 0)],
  [/Log$|Planks$/, wood(2)],
  [/Door$/, wood(3)],
  [/^Bookshelf$/, wood(1.5)],
  [/^Crafting Table$|^Chest$/, wood(2.5)],
  [/^Enchanting Table$/, rock(5, 0)],
  [/^(Chipped |Damaged )?Anvil$/, rock(5, 0)],
  [/^Smithing Table$/, wood(2.5)],
  [/^Red Bed$/, wood(0.2)],
  [/^(Pumpkin|Jack o'Lantern|Melon)$/, wood(1)],
  [/Mushroom Block$|^Mushroom Stem$/, wood(0.2)],
  [/^Bamboo$/, wood(1)],
  [/^Cactus$/, none(0.4)],
  // hoe / shears
  [/Leaves$/, hoe(0.2)],
  [/^Hay Bale$/, hoe(0.5)],
  [/^Sponge$/, hoe(0.6)],
  [/^Moss (Block|Carpet)$/, hoe(0.1)],
  [/^Dried Kelp Block$/, hoe(0.5)],
  [/^Sculk$/, hoe(0.2)],
  [/^Sculk (Shrieker|Catalyst)$/, hoe(3)],
  [/Wool$/, { h: 0.8, tool: 'shears' }],
  [/^Cobweb$/, { h: 4, tool: 'sword', need: 0 }],
  // shovel
  [/^(Grass Block|Snowy Grass Block|Gravel|Clay|Mycelium)$/, dirt(0.6)],
  [/^(Dirt|Coarse Dirt|Rooted Dirt|Podzol|Sand|Red Sand|Mud|Soul Sand|Soul Soil)$/, dirt(0.5)],
  [/^Dirt Path$/, dirt(0.65)],
  [/^Farmland$/, dirt(0.6)],
  [/^Snow Block$/, { h: 0.2, tool: 'shovel', need: 0 }],
  [/^Snow$/, { h: 0.1, tool: 'shovel', need: 0 }],
  [/^Powder Snow$/, dirt(0.25)],
  // instant-break plants, torches, TNT, fire
  [/^(Fire|TNT)$/, none(0)],
  [/Torch$|Grass$|Fern$|Poppy|Dandelion|Orchid|Allium|Bluet|Tulip|Daisy|Cornflower|Lily|Dead Bush|Mushroom$|Sapling|Sunflower|Rose Bush|Lilac|Peony|Azalea$|Kelp$|Seagrass|Sugar Cane|Sweet Berry|Cave Vines|Petals|Litter|Wildflowers|Wheat|Carrots|Potatoes|Beetroots/, none(0)],
  // shaped stone blocks (slab, stairs, wall) whose base block has no rule of its own
  [/ (Slab|Stairs|Wall)$/, rock(2)],
];

const FALLBACK: Spec = none(1);
const SPECS: Spec[] = [];
/** Names that matched no rule (smoke test asserts this stays empty). */
export const UNCLASSIFIED: string[] = [];
for (let id = 0; id < BLOCKS.length; id++) {
  const b = BLOCKS[id];
  if (!b) { SPECS[id] = FALLBACK; continue; }
  // "Fallen Oak Log" etc. are logs
  const name = b.name.replace(/^Fallen /, '');
  const hit = RULES.find(([re]) => re.test(name));
  if (!hit) UNCLASSIFIED.push(`${id}:${b.name}`);
  SPECS[id] = hit ? hit[1] : FALLBACK;
}
SPECS[BEDROCK_ID] = FLUID;
// slabs, stairs and walls are as hard as the block they are cut from (stone-like ones keep its tool tier)
SHAPE_STATE.forEach((st, id) => {
  if (!st || (st.kind !== 'slab' && st.kind !== 'stair' && st.kind !== 'wall')) return;
  const base = SPECS[SHAPE_FAMILIES[st.fam].base];
  if (base && base.tool === 'pickaxe') SPECS[id] = base;
});

const specOf = (id: number): Spec => SPECS[id] ?? FALLBACK;

// --- public rules ---------------------------------------------------------------------

export function blockHardness(id: number): number {
  return specOf(id).h;
}

export function requiredTool(id: number): { type: ToolType | null; minTier: number } | null {
  const s = specOf(id);
  if (s.need === undefined) return null;
  return { type: s.tool, minTier: s.need };
}

export function canHarvest(blockId: number, heldId: number): boolean {
  const s = specOf(blockId);
  if (s.h < 0) return false;
  if (s.need === undefined) return true;
  const t = toolOf(heldId);
  if (!t) return false;
  if (blockId === COBWEB_ID) return t.type === 'sword' || t.type === 'shears';
  return t.type === s.tool && t.tier >= s.need;
}

const SWORD_FAST = /Leaves$|^(Pumpkin|Jack o'Lantern|Melon|Bamboo|Sponge)$|Mushroom Block$/;

/** Mining speed multiplier of `held` on this block (1 = bare hand / wrong tool). */
function speedOn(blockId: number, held: ToolInfo | null): number {
  if (!held) return 1;
  const s = specOf(blockId);
  const name = BLOCKS[blockId]?.name ?? '';
  if (held.type === 'shears') {
    if (blockId === COBWEB_ID || /Leaves$/.test(name)) return 15;
    if (/Wool$/.test(name)) return 5;
    return 1;
  }
  if (held.type === 'sword') {
    if (blockId === COBWEB_ID) return 15;
    return SWORD_FAST.test(name) ? 1.5 : 1;
  }
  return held.type === s.tool ? held.speed : 1;
}

export function breakSeconds(
  blockId: number, heldId: number,
  ctx: { onGround: boolean; inWater: boolean; creative: boolean; efficiency?: number; aquaAffinity?: boolean;
    /** Haste amplifier+1 (0 = none) and Mining Fatigue amplifier+1; hook for the effects system. */ haste?: number; fatigue?: number },
): number {
  const h = specOf(blockId).h;
  if (h < 0) return Infinity;
  if (ctx.creative || h === 0) return 0;
  const harvest = canHarvest(blockId, heldId);
  let speed = speedOn(blockId, toolOf(heldId));
  // Efficiency: + lvl^2 + 1 when the tool is already fast on this block
  if (speed > 1 && ctx.efficiency) speed += ctx.efficiency * ctx.efficiency + 1;
  if (ctx.haste) speed *= 1 + 0.2 * ctx.haste;
  if (ctx.fatigue) speed *= [0.3, 0.09, 0.0027, 0.00081][Math.min(3, ctx.fatigue - 1)];
  if (!ctx.onGround) speed /= 5;
  if (ctx.inWater && !ctx.aquaAffinity) speed /= 5;
  // damage per tick = speed / hardness / (30 | 100); the block goes when it reaches 1
  const ticks = Math.ceil((h * (harvest ? 30 : 100)) / speed);
  return Math.max(1, ticks) / 20;
}

export function toolWear(blockId: number, heldId: number): number {
  if (specOf(blockId).h <= 0) return 0;
  const t = toolOf(heldId);
  if (!t) return 0;
  return t.type === 'sword' ? 2 : 1;
}

// --- canonical ids ---------------------------------------------------------------------

const CANON: number[] = [];
function canon(id: number, to: number): void { CANON[id] = to; }
for (const f of CHEST_FACINGS) canon(f, CHEST_FACINGS[0]);
canon(CHEST_OPEN_FACING_0, CHEST_FACINGS[0]); canon(CHEST_OPEN_FACING_1, CHEST_FACINGS[0]);
canon(CHEST_OPEN_FACING_2, CHEST_FACINGS[0]); canon(CHEST_OPEN_FACING_3, CHEST_FACINGS[0]);
for (const f of FURNACE_FACINGS) canon(f, FURNACE_ID);
for (const k of FURNACE_KINDS) for (const f of [...k.unlit, ...k.lit]) canon(f, k.unlit[0]);
for (const fam of ANVIL_FAMILIES) for (const f of fam) canon(f, fam[0]);
canon(BED_HEAD_ID, BED_FOOT_ID);
canon(SCULK_SHRIEKER_SUMMON_ID, SCULK_SHRIEKER_ID); // the worldgen shrieker mines to the plain (non-summoning) one
DOOR_STATE.forEach((st, id) => { if (st) canon(id, doorId(st.kind, 0, 0, false, false)); });
for (const [base, walls] of TORCH_WALLS) for (const w of walls) if (w !== undefined) canon(w, base);
for (const group of U_GROUPS) for (const id of group) canon(id, group[0]); // chest halves, sign rotations, cauldron levels...
const FALLEN_TO = [LOG_ID, BIRCH_LOG_ID, SPRUCE_LOG_ID];
FALLEN_LOG_IDS.forEach((pair, i) => { for (const id of pair) canon(id, FALLEN_TO[i]); });
// things with no item form
SHAPE_STATE.forEach((st, id) => { if (st) canon(id, SHAPE_FAMILIES[st.fam].canon); });
// every rail shape mines to the one rail item (the switched-on families to their switched-off one)
RAIL_FAMILIES.forEach((ids, k) => { const to = RAIL_FAMILIES[k === 2 ? 1 : k === 4 ? 3 : k === 6 ? 5 : k][0]; for (const id of ids) canon(id, to); });
// redstone: every state mines to the inventory id; dust and tripwire are items, a piston head is part of its piston
for (const g of RS_GROUPS) {
  const k = RS_KIND[g[0]];
  for (const id of g) canon(id, k === K.DUST ? I.REDSTONE : k === K.TRIPWIRE ? I.STRING : k === K.HEAD ? AIR : g[0]);
}
{
  const fam = (name: string): number => SHAPE_FAMILIES.find((f) => f.name === name)!.canon;
  canon(NETHER_BRICK_FENCE_ID, fam('Nether Brick Fence'));
  for (const id of NETHER_BRICK_STAIRS_IDS) canon(id, fam('Nether Brick Stairs'));
  canon(IRON_BARS_ID, fam('Iron Bars'));
}
for (const id of [WATER_ID, LAVA_ID, FALLING_WATER_ID, FIRE_ID, ...FLOWING_WATER_IDS, ...FLOWING_LAVA_IDS, FALLING_LAVA_ID]) canon(id, AIR);

export function blockItemOf(blockId: number): number {
  const c = CANON[blockId];
  return c === undefined ? blockId : c;
}

// --- drops -------------------------------------------------------------------------------

const MUSHROOM_RED = 118;
const MUSHROOM_BROWN = 119;
/** Dark-oak and oak leaves drop apples. */
const APPLE_LEAVES = new Set<number>([7, 87]);

/** Uniform integer in [lo, hi]. */
const rint = (rnd: () => number, lo: number, hi: number): number => lo + Math.floor(rnd() * (hi - lo + 1));

function leafDrops(id: number, heldId: number, rnd: () => number): Stack[] {
  const held = toolOf(heldId);
  if (held?.type === 'shears') return [stackOf(id)];
  const out: Stack[] = [];
  const name = BLOCKS[id].name;
  if (/Azalea/.test(name)) { if (rnd() < 0.05) out.push(stackOf(AZALEA_ID)); }
  else if (rnd() < (/Jungle/.test(name) ? 0.025 : 0.05) && !/Cherry|Mangrove|Pale/.test(name)) out.push(stackOf(OAK_SAPLING_ID));
  if (rnd() < 0.02) out.push(stackOf(I.STICK, rint(rnd, 1, 2)));
  if (APPLE_LEAVES.has(id) && rnd() < 0.005) out.push(stackOf(I.APPLE));
  return out;
}

export function dropsFor(blockId: number, heldId: number, rnd: () => number): Stack[] {
  const s = specOf(blockId);
  if (s.h < 0 || blockId === AIR) return [];
  if (!canHarvest(blockId, heldId)) return [];
  const held = toolOf(heldId);
  const name = BLOCKS[blockId]?.name ?? '';
  const one = (id: number, n = 1): Stack[] => [stackOf(id, n)];

  const shaped = SHAPE_STATE[blockId];
  if (shaped?.kind === 'slab' && shaped.half === 2) return one(SHAPE_FAMILIES[shaped.fam].canon, 2); // a double slab is two
  if (shaped?.kind === 'pane' && SHAPE_FAMILIES[shaped.fam].name === 'Glass Pane') return [];
  // two-block structures: only the primary half drops
  if (DOOR_TOP_IDS.has(blockId) || blockId === BED_HEAD_ID) return [];
  if (/Leaves$/.test(name)) return leafDrops(blockId, heldId, rnd);
  if (isCrop(blockId)) return cropDrops(blockId, rnd);
  switch (U_KIND[blockId]) {
    case UK.CAKE: return []; // a cake is eaten, never picked up
    case UK.SHULKER: return []; // the host drops the box with its contents (server/actions/utility.ts)
    case UK.VINE: return held?.type === 'shears' ? one(UTIL.VINE) : [];
    case UK.CANDLE: return one(UTIL.CANDLE, U_A[blockId]);
    case UK.CAMPFIRE: return one(I.CHARCOAL, 2);
    case UK.POT: return U_A[blockId] ? [stackOf(UTIL.POT), stackOf(U_A[blockId])] : one(UTIL.POT);
    default: break;
  }

  switch (blockId) {
    case GRASS_ID: case MYCELIUM_ID: case PODZOL_ID: case SNOW_ID: case DIRT_PATH_ID: case FARMLAND_ID: case FARMLAND_DRY_ID:
      return one(DIRT_ID);
    case STONE_ID: return one(COBBLE_ID);
    case DEEPSLATE_ID: return one(COBBLED_DEEPSLATE_ID);
    case GRAVEL_ID: return rnd() < 0.1 ? one(I.FLINT) : one(GRAVEL_ID);
    case CLAY_ID: return one(I.CLAY_BALL, 4);
    case GLASS_ID: case ICE_ID: case PACKED_ICE_ID: case BLUE_ICE_ID: case POWDER_SNOW_ID:
    case SNOW_BLOCK_ID: case SNOW_LAYER_ID: case SCULK_ID: case FIRE_ID:
      return [];
    case TALL_GRASS_ID: case FERN_ID:
      if (held?.type === 'shears') return one(blockId);
      return rnd() < 0.125 ? one(I.WHEAT_SEEDS) : [];
    case SEAGRASS_ID:
      return held?.type === 'shears' ? one(blockId) : [];
    case DEAD_BUSH_ID:
      if (held?.type === 'shears') return one(blockId);
      { const n = rint(rnd, 0, 2); return n ? one(I.STICK, n) : []; }
    case COBWEB_ID:
      return held?.type === 'shears' ? one(COBWEB_ID) : one(I.STRING);
    case WHEAT_ID:
      // fully grown wheat: 1 wheat + 1..4 seeds (binomial 3 @ 0.5714 + 1)
      return cropDrops(blockId, rnd);
    case MELON_ID: return one(I.MELON_SLICE, rint(rnd, 3, 7));
    case RED_MUSHROOM_BLOCK_ID: { const n = rint(rnd, 0, 2); return n ? one(MUSHROOM_RED, n) : []; }
    case BROWN_MUSHROOM_BLOCK_ID: { const n = rint(rnd, 0, 2); return n ? one(MUSHROOM_BROWN, n) : []; }
    // ores
    case 14: case 66: return one(I.COAL);
    case 15: case 67: return one(I.RAW_IRON);
    case 16: case 69: return one(I.RAW_GOLD);
    case 17: case 73: return one(I.DIAMOND);
    case 62: case 68: return one(I.RAW_COPPER, rint(rnd, 2, 5));
    case 63: case 70: return one(I.REDSTONE, rint(rnd, 4, 5));
    case 64: case 71: return one(I.LAPIS_LAZULI, rint(rnd, 4, 9));
    case 65: case 72: return one(I.EMERALD);
    default: break;
  }
  if (blockId === QUARTZ_ORE_ID) return one(I.QUARTZ);
  if (blockId === GLOWSTONE_ID) return one(I.GLOWSTONE_DUST, rint(rnd, 2, 4));
  if (BLOCKS[blockId]?.name === 'Nether Gold Ore') return one(I.GOLD_NUGGET, rint(rnd, 2, 6));
  if (BLOCKS[blockId]?.name === 'Nether Portal') return [];
  if (BLOCKS[blockId]?.name === 'Nether Wart') {
    const seed = BLOCKS.findIndex((b) => b?.name === 'Nether Wart');
    // mature (the last age id) drops 2-4, younger ones give the wart back
    const mature = BLOCKS[blockId + 1]?.name !== 'Nether Wart';
    return one(seed, mature ? rint(rnd, 2, 4) : 1);
  }
  if (BLOCKS[blockId]?.name === 'Spawner' || BLOCKS[blockId]?.name === 'Soul Fire') return [];
  if (/^(Weeping|Twisting) Vines Plant$/.test(BLOCKS[blockId]?.name ?? '')) return [];
  if (name === 'Cave Vines') return one(I.GLOW_BERRIES);
  if (blockId === BOOKSHELF_ID) return one(I.BOOK, 3);
  if (blockId === QUARTZ_ORE_ID || blockId === GLOWSTONE_ID || blockId === BOOKSHELF_ID || blockId === SEA_LANTERN_ID) {
    // vanilla drops quartz / glowstone dust / books / crystals, none of which exist here
    return one(blockId);
  }
  if (/Stem$/.test(name) && !/^(Crimson|Warped) Stem$/.test(name)) return [];
  const item = blockItemOf(blockId);
  if (item === AIR) return [];
  return one(item);
}

// --- XP -------------------------------------------------------------------------------------

export function experienceFor(blockId: number, rnd: () => number): number {
  switch (blockId) {
    case 14: case 66: return rint(rnd, 0, 2);
    case 63: case 70: return rint(rnd, 1, 5);
    case 64: case 71: return rint(rnd, 2, 5);
    case 17: case 73: case 65: case 72: return rint(rnd, 3, 7);
    case QUARTZ_ORE_ID: return rint(rnd, 2, 5);
    case SCULK_ID: return 1;
    case SCULK_SHRIEKER_ID: case SCULK_SHRIEKER_SUMMON_ID: case SCULK_CATALYST_ID: return 5;
    default: return 0;
  }
}
